import type { ExtractSwarovskiPageData, EventPageData } from "@swo/storefront";
import type React from "react";
import { transformImageIfFromContentful_jpg } from "../../../domain/contentful/transform-image-if-from-contentful";
import type { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import { getSwarovskiOptikHeadTitle } from "../../_layout-helpers/head-title";

type Event = ExtractSwarovskiPageData<EventPageData>["data"]["event"];

export const EventMetadata: ReactTFC<{
  event: Event;
  pathname: string;
}> = ({ event, pathname }) => {
  const eventImageUrl = event.imageUrl;

  const eventImageUrl_og = transformImageIfFromContentful_jpg(eventImageUrl, 1200);

  const obj = {
    "@context": "https://schema.org",
    "@type": "Event",
    id: event.id,
    name: event.name,
    url: event.eventExternalLink ?? `https://www.swarovskioptik.com${pathname}`,

    startDate: event.startDate,
    endDate: event.endDate,
    ...(event.allDayEvent && {
      duration: "PT24H",
    }),

    ...(event.subject && { keywords: event.subject }),
    ...(event.targetGroups && {
      audience: {
        "@type": "Audience",
        audienceType: event.targetGroups.join(", "),
      },
    }),

    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        addressCountry: event.country,
        ...(event.coordinates && {
          geo: {
            "@type": "GeoCoordinates",
            latitude: event.coordinates.latitude,
            longitude: event.coordinates.longitude,
          },
        }),
      },
    },
    image: [eventImageUrl_og],
    description: event.description,
    organizer: {
      "@type": "Organization",
      name: "Swarovski Optik",
      url: "https://www.swarovskioptik.com/",
    },
  };

  return (
    <>
      <title>{getSwarovskiOptikHeadTitle(event.name)}</title>
      <meta name="description" content={`${event.description} - Swarovski Optik`} />
      <meta property="og:image" content={eventImageUrl_og} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(obj) }} />
    </>
  );
};
